import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../../Authentication/LanguageContext";
import ServerUrl from "../../../../../BACKEND";
import "./Family.css";

interface FamilyItem {
  id: number;
  german: string;
  sentence: string;
  audio: string | null;
  image: string | null;
  tamil: string;
  english: string;
  sinhala: string;
  hindi: string;
  arabic: string;
  turkish: string;
  russian: string;
  chinese: string;
  french: string;
  spanish: string;
  [key: string]: string | number | null;
}

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

const Family: React.FC = () => {
  const [data, setData] = useState<FamilyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const { language } = useLanguage();

  useEffect(() => {
    fetch(`${ServerUrl}/api/family`)
      .then((res) => res.json())
      .then((json) => setData(json))
      .catch((err) => console.error("Error fetching family:", err))
      .finally(() => setLoading(false));
  }, [language]);

  const getTranslation = (item: FamilyItem) => {
    const map: Record<string, string> = {
      en: "english",
      de: "german",
      ta: "tamil",
      si: "sinhala",
      hi: "hindi",
      ar: "arabic",
      tr: "turkish",
      ru: "russian",
      zh: "chinese",
      fr: "french",
      es: "spanish",
    };
    return capitalize(String(item[map[language] || "english"] || ""));
  };

  const playAudioOrTTS = (item: FamilyItem) => {
    if (currentlyPlaying === item.german) {
      setCurrentlyPlaying(null);
      return;
    }

    setCurrentlyPlaying(item.german);

    if (item.audio) {
      const audio = new Audio(`${ServerUrl}/storage/${item.audio}`);
      audio.play();
      audio.onended = () => setCurrentlyPlaying(null);
      audio.onerror = () => {
        speak(item.german);
      };
    } else {
      speak(item.german);
    }
  };

  const speak = (text: string) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "de-DE";
    utterance.onend = () => setCurrentlyPlaying(null);
    speechSynthesis.speak(utterance);
  };

  const getImageUrl = (image: string | null) => {
    if (!image) return `${ServerUrl}/storage/A1Image/placeholder.png`;
    if (image.startsWith("http")) return image;
    return `${ServerUrl}/storage/${image}`;
  };

  const titleMap = {
    en: "Family",
    de: "Familie",
    ta: "குடும்பம்",
    si: "පවුල",
    hi: "परिवार",
    ar: "عائلة",
    tr: "Aile",
    ru: "Семья",
    zh: "家庭",
    fr: "Famille",
    es: "Familia",
  };

  const subtitleMap = {
    en: "Family Members in German",
    de: "Familienmitglieder",
    ta: "ஜெர்மன் மொழியில் குடும்ப உறுப்பினர்கள்",
    si: "ජර්මන් භාෂාවෙන් පවුලේ සාමාජිකයන්",
    hi: "जर्मन में परिवार के सदस्य",
    ar: "أفراد العائلة بالألمانية",
    tr: "Almanca'da Aile Üyeleri",
    ru: "Члены семьи на немецком",
    zh: "德语家庭成员",
    fr: "Membres de la famille en allemand",
    es: "Miembros de la familia en alemán",
  };

  return (
    <div className="family-page-container">
      <div className="family-page-header">
        <div>
          <h1 className="family-page-title">{titleMap["de"]}</h1>
          <p className="family-page-subtitle">{subtitleMap[language]}</p>
        </div>
      </div>

      {loading ? (
        <div className="family-page-loading">Loading...</div>
      ) : (
        <div className="family-page-grid">
          {data.map((item) => {
            const isPlaying = currentlyPlaying === item.german;
            return (
              <div
                key={item.id}
                className={`family-page-card ${
                  isPlaying ? "family-page-playing" : ""
                }`}
                onClick={() => playAudioOrTTS(item)}
              >
                <div className="family-page-image-wrapper">
                  <img
                    src={getImageUrl(item.image)}
                    alt={item.german}
                    className="family-page-image"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `${ServerUrl}/storage/A1Image/placeholder.png`;
                    }}
                  />
                </div>
                <div className="family-page-german-text">
                  {capitalize(item.german)}
                </div>
                <div className="family-page-translation-text">
                  {getTranslation(item)}
                </div> 
                <div className="family-page-sentence-text">{item.sentence}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Family; 